import { dequal } from "dequal";
import DRange from "drange";
import { configKeys, ANON_USER_ID, anonymousUser, applyOptionUpdate, AuthProvider, chatIdentifiersEqual, chatIdentifierToString, ChatListScopeMap, ChatMap, ChatSet, CommunityMap, DEFAULT_TOKENS, emptyChatMetrics, emptyDailyPuzzleState, getDisplayDate, Immutable, LEDGER_CANISTER_CKBTC, LEDGER_CANISTER_CKETH, LEDGER_CANISTER_ICP, mergeListOfCombinedUnreadCounts, mergePairOfCombinedUnreadCounts, messageContextsEqual, MessageMap, ModerationFlags, PremiumItem, SafeMap, videoCallsInProgressForChats, } from "@shared";
import { createSetStore } from "../../stores/setStore";
import { getMessagePermissionsForSelectedChat, mergeChatMetrics, mergeEventsAndLocalUpdatesWithRange, mergePermissions, mergeUnconfirmedIntoSummary, } from "../../utils/chat";
import { enumFromStringValue } from "../../utils/enums";
import { derived, writable } from "../../utils/stores";
import { nullProfile } from "../../utils/user";
import { chatDetailsLocalUpdates } from "../chat/detailsUpdates";
import { chatSummaryLocalUpdates, ChatSummaryUpdates } from "../chat/summaryUpdates";
import { communityLocalUpdates } from "../community/detailUpdates";
import { communitySummaryLocalUpdates } from "../community/summaryUpdates";
import { LocalStorageBoolStore, LocalStorageStore } from "../localStorageStore";
import { localUpdates } from "../localUpdates";
import { messageLocalUpdates } from "../message/localUpdates";
import { routeStore, selectedCommunityIdStore } from "../path/stores";
import { expiredServerEventRanges, selectedServerChatStore, serverEventsStore, serverThreadEventsStore, startChatVisit, startThreadVisit, } from "../selectedChat";
import { SnsFunctions } from "../snsFunctions.svelte";
import { messagesRead } from "../unread/markRead";
import { blockedUsersStore, suspendedUsersStore } from "../users/stores";
import { notEq } from "../utils";
export const ONE_MB = 1024 * 1024;
export const ONE_GB = ONE_MB * 1024;
function communityFilterFromString(serialised) {
    const parsed = JSON.parse(serialised);
    return new Set(parsed.languages);
}
function communityFilterToString(filter) {
    return JSON.stringify({
        ...filter,
        languages: Array.from(filter),
    });
}
export const hideMessagesFromDirectBlocked = new LocalStorageBoolStore(configKeys.hideBlocked, false);
export const lastSelectedChatByScopeStore = writable(new ChatListScopeMap(), undefined, notEq);
export const lastSelectedCommunityIdStore = writable(undefined);
export const cryptoLookup = writable(new SafeMap(), undefined, notEq);
export const nervousSystemLookup = writable(new SafeMap(), undefined, notEq);
// whether the first attempt to load the exchange rates has finished (they may still have failed)
export const exchangeRatesTriedStore = writable(false);
export const exchangeRatesLookupStore = writable(new SafeMap(), undefined, notEq);
// The largest coins OpenChat doesn't list (eg. ADA), keyed by symbol in lower case, for cashtags
export const cashtagCoinsStore = writable(new SafeMap(), undefined, notEq);
// Where prices come from, keyed as the prices' `source`s
export const priceSourcesStore = writable(new SafeMap(), undefined, notEq);
// The cards of games in messages which the MiniApp service is showing live, for the chat being
// viewed, keyed by `miniAppGameKey`
export const miniAppCardsStore = writable(new Map(), undefined, notEq);
function createCryptoBalanceStore() {
    const store = writable(new Map(), undefined, notEq);
    return {
        get value() {
            return store.value;
        },
        subscribe: (sub, invalidate) => store.subscribe(sub, invalidate),
        setBalance(ledger, balance) {
            if (store.value.get(ledger) !== balance) {
                store.update((s) => s.set(ledger, balance));
            }
            cryptoBalancesLastUpdated.set(ledger, Date.now());
        },
        valueIfUpdatedRecently(ledger) {
            const lastUpdated = cryptoBalancesLastUpdated.get(ledger);
            if (lastUpdated === undefined) {
                return undefined;
            }
            return Date.now() - lastUpdated < 5 * 60 * 1000 ? store.value.get(ledger) : undefined;
        },
    };
}
export const cryptoBalanceStore = createCryptoBalanceStore();
const cryptoBalancesLastUpdated = new Map();
export const bitcoinAddress = writable(undefined);
export const oneSecAddress = writable(undefined);
export const solanaAddress = writable(undefined);
export const namedAccountsStore = writable([], undefined, notEq);
export const lastCryptoSent = new LocalStorageStore(configKeys.lastCryptoSent, undefined);
// The ledgers whose exchange rates balances are converted at. ckBTC and ckETH are backed 1:1 by
// BTC and ETH, so their prices are those of BTC and ETH
export const conversionLedgers = {
    icp: LEDGER_CANISTER_ICP,
    btc: LEDGER_CANISTER_CKBTC,
    eth: LEDGER_CANISTER_CKETH,
};
export function getConvertedBalances(xr, balance, decimals, ledger) {
    const xrICPtoDollar = xr.get(conversionLedgers.icp)?.toUSD;
    const xrBTCtoDollar = xr.get(conversionLedgers.btc)?.toUSD;
    const xrETHtoDollar = xr.get(conversionLedgers.eth)?.toUSD;
    // Without a rate (or with a rate of 0) a converted balance is unknown rather than 0
    const xrDollarToICP = xrICPtoDollar ? 1 / xrICPtoDollar : undefined;
    const xrDollarToBTC = xrBTCtoDollar ? 1 / xrBTCtoDollar : undefined;
    const xrDollarToETH = xrETHtoDollar ? 1 / xrETHtoDollar : undefined;
    const balanceWholeUnits = Number(balance) / Math.pow(10, decimals);
    const xrUSD = xr.get(ledger)?.toUSD;
    const dollarBalance = xrUSD !== undefined ? xrUSD * balanceWholeUnits : undefined;
    const icpBalance = dollarBalance !== undefined && xrDollarToICP !== undefined
        ? dollarBalance * xrDollarToICP
        : undefined;
    const btcBalance = dollarBalance !== undefined && xrDollarToBTC !== undefined
        ? dollarBalance * xrDollarToBTC
        : undefined;
    const ethBalance = dollarBalance !== undefined && xrDollarToETH !== undefined
        ? dollarBalance * xrDollarToETH
        : undefined;
    return {
        dollarBalance,
        icpBalance,
        btcBalance,
        ethBalance,
    };
}
export const enhancedCryptoLookup = derived([cryptoLookup, cryptoBalanceStore, exchangeRatesLookupStore], ([$lookup, $balance, $exchangeRatesLookup]) => {
    return [...$lookup.entries()].reduce((result, [key, t]) => {
        const balance = $balance.get(t.ledger) ?? BigInt(0);
        const balances = getConvertedBalances($exchangeRatesLookup, balance, t.decimals, t.ledger);
        const zero = balance === BigInt(0) && !DEFAULT_TOKENS.includes(t.symbol);
        result.set(key, {
            ...t,
            balance,
            ...balances,
            zero,
            urlFormat: t.transactionUrlFormat,
        });
        return result;
    }, new Map());
});
export const cryptoTokensSorted = derived(enhancedCryptoLookup, ($lookup) => {
    return [...$lookup.values()].filter((t) => t.enabled || !t.zero).sort(compareTokens);
});
// The token each symbol stands for when someone types one (a cashtag, a bot command's token), keyed
// by the symbol in lower case. Several ledgers can share a symbol, so prefer an enabled one, then the
// first in the wallet's order, which puts the tokens the user holds first
export const tokensBySymbolStore = derived(cryptoTokensSorted, ($tokens) => {
    const bySymbol = new Map();
    for (const t of $tokens) {
        const symbol = t.symbol.toLowerCase();
        const current = bySymbol.get(symbol);
        if (current === undefined || (!current.enabled && t.enabled)) {
            bySymbol.set(symbol, t);
        }
    }
    return bySymbol;
});
export function walletTokenBySymbol(symbol) {
    return tokensBySymbolStore.value.get(symbol.toLowerCase());
}
function meetsAutoWalletCriteria(config, token) {
    return (config.kind === "auto_wallet" &&
        (DEFAULT_TOKENS.includes(token.symbol) ||
            (config.minDollarValue <= 0 && token.balance > 0) ||
            (config.minDollarValue > 0 && (token.dollarBalance ?? 0) >= config.minDollarValue)));
}
function meetsManualWalletCriteria(config, token) {
    return config.kind === "manual_wallet" && config.tokens.has(token.ledger);
}
export const serverWalletConfigStore = writable({
    kind: "auto_wallet",
    minDollarValue: 0,
}, undefined, notEq);
export const walletConfigStore = derived([serverWalletConfigStore, localUpdates.walletConfig], ([serverWalletConfig, localUpates]) => localUpates ?? serverWalletConfig);
export const walletTokensSorted = derived([cryptoTokensSorted, walletConfigStore], ([$tokens, walletConfig]) => {
    return $tokens.filter((t) => meetsAutoWalletCriteria(walletConfig, t) ||
        meetsManualWalletCriteria(walletConfig, t));
});
export const swappableTokensStore = writable(new Set(), undefined, notEq);
// The DEXes the wallet swaps with
export const swapProvidersStore = writable([], undefined, notEq);
function compareTokens(a, b) {
    // Sort by non-zero balances first
    // Then by $ balance
    // Then by whether token is a default
    // Then by default precedence
    // Then alphabetically by symbol
    const aNonZero = a.balance > 0;
    const bNonZero = b.balance > 0;
    if (aNonZero !== bNonZero) {
        return aNonZero ? -1 : 1;
    }
    const aDollarBalance = a.dollarBalance ?? -1;
    const bDollarBalance = b.dollarBalance ?? -1;
    if (aDollarBalance < bDollarBalance) {
        return 1;
    }
    else if (aDollarBalance > bDollarBalance) {
        return -1;
    }
    else {
        const defA = DEFAULT_TOKENS.indexOf(a.symbol);
        const defB = DEFAULT_TOKENS.indexOf(b.symbol);
        if (defA >= 0 && defB >= 0) {
            return defA < defB ? 1 : -1;
        }
        else if (defA >= 0) {
            return 1;
        }
        else if (defB >= 0) {
            return -1;
        }
        else {
            return a.symbol.localeCompare(b.symbol);
        }
    }
}
export function hasFlag(mask, flag) {
    return (mask & flag) !== 0;
}
export const pinNumberRequiredStore = writable(undefined);
export const pinNumberResolverStore = writable(undefined, undefined, notEq);
export const pinNumberFailureStore = writable(undefined, undefined, notEq);
export const storageStore = writable({
    byteLimit: 0,
    bytesUsed: 0,
}, undefined, notEq);
export const percentageStorageUsedStore = derived(storageStore, (storage) => Math.ceil((storage.bytesUsed / storage.byteLimit) * 100));
export const percentageStorageRemainingStore = derived(storageStore, (storage) => Math.floor((1 - storage.bytesUsed / storage.byteLimit) * 100));
export const storageInGBStore = derived(storageStore, (storage) => ({
    gbLimit: storage.byteLimit / ONE_GB,
    gbUsed: storage.bytesUsed / ONE_GB,
}));
export const messageFiltersStore = writable([], undefined, notEq);
export const translationsStore = writable(new MessageMap(), undefined, notEq);
export const snsFunctionsStore = writable(new SnsFunctions(), undefined, notEq);
export const currentUserStore = writable(anonymousUser(), undefined, notEq);
export const currentUserProfileStore = writable(nullProfile(), undefined, notEq);
export const currentUserIdStore = derived(currentUserStore, ({ userId }) => userId);
export const anonUserStore = derived(currentUserIdStore, (id) => id === ANON_USER_ID);
export const suspendedUserStore = derived(currentUserStore, (user) => user.suspensionDetails !== undefined);
export const platformModeratorStore = derived(currentUserStore, (user) => user.isPlatformModerator);
export const platformOperatorStore = derived(currentUserStore, (user) => user.isPlatformOperator);
export const diamondStatusStore = derived(currentUserStore, (user) => user.diamondStatus);
export const isDiamondStore = derived(diamondStatusStore, (diamondStatus) => diamondStatus.kind === "lifetime" ||
    (diamondStatus.kind === "active" && diamondStatus.expiresAt > Date.now()));
export const isLifetimeDiamondStore = derived(diamondStatusStore, (diamondStatus) => diamondStatus.kind === "lifetime");
export const canExtendDiamondStore = derived(diamondStatusStore, (diamondStatus) => diamondStatus.kind === "active");
export const moderationFlagsEnabledStore = derived(currentUserStore, ({ moderationFlagsEnabled }) => moderationFlagsEnabled);
export const adultEnabledStore = derived(moderationFlagsEnabledStore, (moderationFlagsEnabled) => hasFlag(moderationFlagsEnabled, ModerationFlags.Adult));
export const offensiveEnabledStore = derived(moderationFlagsEnabledStore, (moderationFlagsEnabled) => hasFlag(moderationFlagsEnabled, ModerationFlags.Offensive));
export const underReviewEnabledStore = derived(moderationFlagsEnabledStore, (moderationFlagsEnabled) => hasFlag(moderationFlagsEnabled, ModerationFlags.UnderReview));
const notificationsSupported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
export const softDisabledStore = new LocalStorageBoolStore(configKeys.softDisabled, false);
const browserPermissionStore = writable("pending-init");
export async function initNotificationStores() {
    if (!notificationsSupported) {
        return;
    }
    console.debug("PUSH: initialising notification stores with ", Notification.permission);
    browserPermissionStore.set(Notification.permission);
    if (navigator.permissions) {
        navigator.permissions.query({ name: "notifications" }).then((perm) => {
            perm.onchange = () => {
                console.debug("PUSH: permission status changed to ", perm.state);
                browserPermissionStore.set(permissionStateToNotificationPermission(perm.state));
            };
        });
    }
}
export function setSoftDisabled(softDisabled) {
    softDisabledStore.set(softDisabled);
}
function permissionStateToNotificationPermission(perm) {
    switch (perm) {
        case "prompt":
            return "default";
        case "denied":
            return "denied";
        case "granted":
            return "granted";
    }
}
function permissionToStatus(permission) {
    switch (permission) {
        case "pending-init":
            return "pending-init";
        case "denied":
            return "hard-denied";
        case "granted":
            return "granted";
        default:
            return "prompt";
    }
}
export const notificationStatus = derived([softDisabledStore, browserPermissionStore, anonUserStore], ([softDisabled, browserPermission, anonUser]) => {
    if (!notificationsSupported || anonUser) {
        return "unsupported";
    }
    if (softDisabled) {
        return "soft-denied";
    }
    return permissionToStatus(browserPermission);
});
export async function askForNotificationPermission() {
    return Notification.requestPermission()
        .then((res) => {
        console.debug("PUSH: requestPermission result: ", res);
        setSoftDisabled(false);
        browserPermissionStore.set(res);
        return res;
    })
        .catch((err) => {
        console.debug("PUSH: requestPermission err: ", err);
        throw err;
    });
}
export const showUnpublishedBots = new LocalStorageBoolStore("openchat_unpublished_bots", true);
export const communityFiltersStore = new LocalStorageStore("openchat_community_filters", new Set(), communityFilterToString, communityFilterFromString, notEq);
export const exploreCommunitiesFiltersStore = derived([communityFiltersStore, moderationFlagsEnabledStore], ([communityFilters, moderationFlagsEnabled]) => ({
    languages: Array.from(communityFilters),
    flags: moderationFlagsEnabled,
}));
export const userCreatedStore = new LocalStorageBoolStore(configKeys.userCreated, false);
export const selectedAuthProviderStore = new LocalStorageStore(configKeys.selectedAuthProvider, AuthProvider.PASSKEY, (a) => a, (a) => enumFromStringValue(AuthProvider, a, AuthProvider.PASSKEY));
export const achievementsStore = createSetStore();
export const premiumItemsStore = createSetStore();
export const chitStateStore = writable({
    chitBalance: 0,
    totalChitEarned: 0,
    streak: 0,
    maxStreak: 0,
    streakEnds: 0n,
    nextDailyChitClaim: 0n,
}, undefined, notEq);
export const dailyPuzzleStore = writable(emptyDailyPuzzleState, undefined, notEq);
export const serverCommunitiesStore = writable(new CommunityMap(), undefined, notEq);
export const communitiesStore = derived([
    serverCommunitiesStore,
    localUpdates.communities,
    localUpdates.previewCommunities,
    communitySummaryLocalUpdates,
], ([serverCommunities, localCommunities, previewCommunities, localUpdates]) => {
    const merged = localCommunities.apply(serverCommunities.clone().merge(previewCommunities));
    return [...merged.entries()].reduce((result, [communityId, community]) => {
        const updates = localUpdates.get(communityId);
        const anyChanges = updates?.index !== undefined ||
            updates?.displayName !== undefined ||
            updates?.rulesAccepted !== undefined;
        if (anyChanges) {
            // only membership is modified below so that is all we need to copy
            const clone = { ...community, membership: { ...community.membership } };
            const index = updates?.index;
            if (index !== undefined) {
                clone.membership.index = index;
            }
            clone.membership.displayName = applyOptionUpdate(clone.membership.displayName, updates?.displayName);
            clone.membership.rulesAccepted =
                updates?.rulesAccepted ?? clone.membership.rulesAccepted;
            result.set(communityId, clone);
        }
        else {
            result.set(communityId, community);
        }
        return result;
    }, new CommunityMap());
});
export const sortedCommunitiesStore = derived(communitiesStore, (communities) => {
    return [...communities.values()].sort((a, b) => {
        return b.membership.index === a.membership.index
            ? b.memberCount - a.memberCount
            : b.membership.index - a.membership.index;
    });
});
export const nextCommunityIndexStore = derived(sortedCommunitiesStore, (sortedCommunitiesStore) => (sortedCommunitiesStore[0]?.membership?.index ?? -1) + 1);
export const userGroupSummariesStore = derived(communitiesStore, (communities) => {
    return [...communities.values()].reduce((map, community) => {
        community.userGroups.forEach((ug) => map.set(ug.id, ug));
        return map;
    }, new Map());
}, notEq);
export const selectedChatIdStore = derived(routeStore, (route) => {
    switch (route.kind) {
        case "selected_channel_route":
        case "global_chat_selected_route":
            return route.chatId;
        default:
            return undefined;
    }
}, chatIdentifiersEqual);
export const selectedThreadIdStore = writable(undefined, undefined, messageContextsEqual);
// Whenever the selectedChatIdStore value changes the first thing we do is start a new visit, which
// clears the related stores
selectedChatIdStore.subscribe((chatId) => {
    startChatVisit(chatId);
    selectedThreadIdStore.set(undefined);
});
// Whenever the selectedThreadIdStore value changes we start a new visit to the thread, which clears
// the serverThreadEventsStore
selectedThreadIdStore.subscribe((thread) => startThreadVisit(thread));
export const chatListScopeStore = derived(routeStore, (route) => route.scope, dequal);
export const chatsInitialisedStore = writable(false);
export const selectedServerCommunityStore = writable(undefined, undefined, notEq);
// Whenever the selectedCommunityIdStore value changes we immediately clear the selectedServerCommunityStore
selectedCommunityIdStore.subscribe((_) => selectedServerCommunityStore.set(undefined));
export const selectedCommunityMembersStore = derived([selectedServerCommunityStore, communityLocalUpdates.members], ([community, members]) => {
    if (community === undefined)
        return new Map();
    const updates = members.get(community.communityId);
    if (updates === undefined)
        return community.members;
    return updates.apply(community.members);
}, notEq);
export const selectedCommunityBotsStore = derived([selectedServerCommunityStore, communityLocalUpdates.bots], ([community, bots]) => {
    if (community === undefined)
        return new Map();
    const updates = bots.get(community.communityId);
    if (updates === undefined)
        return community.bots;
    return updates.apply(community.bots);
}, notEq);
export const selectedCommunityUserGroupsStore = derived([selectedServerCommunityStore, communityLocalUpdates.userGroups], ([community, userGroups]) => {
    if (community === undefined)
        return new Map();
    const updates = userGroups.get(community.communityId);
    if (updates === undefined)
        return community.userGroups;
    return updates.apply(community.userGroups);
}, notEq);
export const selectedCommunityInvitedUsersStore = derived([selectedServerCommunityStore, communityLocalUpdates.invitedUsers], ([community, invitedUsers]) => {
    if (community === undefined)
        return new Set();
    const updates = invitedUsers.get(community.communityId);
    if (updates === undefined)
        return community.invitedUsers;
    return updates.apply(community.invitedUsers);
}, notEq);
export const selectedCommunityBlockedUsersStore = derived([selectedServerCommunityStore, communityLocalUpdates.blockedUsers], ([community, blockedUsers]) => {
    if (community === undefined)
        return new Set();
    const updates = blockedUsers.get(community.communityId);
    if (updates === undefined)
        return community.blockedUsers;
    return updates.apply(community.blockedUsers);
}, notEq);
export const selectedCommunityRulesStore = derived([selectedServerCommunityStore, communityLocalUpdates.rules], ([community, rules]) => {
    if (community === undefined)
        return undefined;
    const updates = rules.get(community.communityId);
    return updates ?? community.rules;
}, notEq);
export const selectedCommunityLapsedMembersStore = derived(selectedServerCommunityStore, (selectedCommunity) => selectedCommunity?.lapsedMembers ?? new Set(), notEq);
export const selectedCommunityReferralsStore = derived(selectedServerCommunityStore, (selectedCommunity) => selectedCommunity?.referrals ?? new Set(), notEq);
export const selectedCommunitySummaryStore = derived([selectedCommunityIdStore, communitiesStore], ([selectedCommunityId, communities]) => selectedCommunityId ? communities.get(selectedCommunityId) : undefined, notEq);
export const selectedChatMembersStore = derived([selectedServerChatStore, chatDetailsLocalUpdates.members], ([chat, members]) => {
    if (chat === undefined)
        return new Map();
    return members.get(chat.chatId)?.apply(chat.members) ?? chat.members;
}, notEq);
export const selectedChatBlockedUsersStore = derived([selectedServerChatStore, chatDetailsLocalUpdates.blockedUsers], ([chat, blockedUsers]) => {
    if (chat === undefined)
        return new Set();
    return blockedUsers.get(chat.chatId)?.apply(chat.blockedUsers) ?? chat.blockedUsers;
}, notEq);
export const selectedChatLapsedMembersStore = derived([selectedServerChatStore, chatDetailsLocalUpdates.lapsed], ([chat, lapsed]) => {
    if (chat === undefined)
        return new Set();
    return lapsed.get(chat.chatId)?.apply(chat.lapsedMembers) ?? chat.lapsedMembers;
}, notEq);
export const selectedChatPinnedMessagesStore = derived([selectedServerChatStore, chatDetailsLocalUpdates.pinnedMessages], ([chat, pinnedMessages]) => {
    if (chat === undefined)
        return new Set();
    return pinnedMessages.get(chat.chatId)?.apply(chat.pinnedMessages) ?? chat.pinnedMessages;
}, notEq);
export const selectedChatInvitedUsersStore = derived([selectedServerChatStore, chatDetailsLocalUpdates.invitedUsers], ([chat, invitedUsers]) => {
    if (chat === undefined)
        return new Set();
    return invitedUsers.get(chat.chatId)?.apply(chat.invitedUsers) ?? chat.invitedUsers;
}, notEq);
export const selectedChatBotsStore = derived([selectedServerChatStore, chatDetailsLocalUpdates.bots], ([chat, bots]) => {
    if (chat === undefined)
        return new Map();
    return bots.get(chat.chatId)?.apply(chat.bots) ?? chat.bots;
}, notEq);
export const selectedChatWebhooksStore = derived([selectedServerChatStore, chatDetailsLocalUpdates.webhooks], ([chat, webhooks]) => {
    if (chat === undefined)
        return new Map();
    return webhooks.get(chat.chatId)?.apply(chat.webhooks) ?? chat.webhooks;
}, notEq);
export const selectedChatRulesStore = derived([selectedServerChatStore, chatDetailsLocalUpdates.rules], ([chat, rules]) => {
    if (chat === undefined)
        return undefined;
    return rules.get(chat.chatId) ?? chat.rules;
}, notEq);
export const serverDirectChatsStore = writable(new ChatMap(), undefined, notEq);
export const serverGroupChatsStore = writable(new ChatMap(), undefined, notEq);
export const serverFavouritesStore = writable(new ChatSet(), undefined, notEq);
export const serverPinnedChatsStore = writable(new Map(), undefined, notEq);
export const serverMessageActivitySummaryStore = writable({
    readUpToTimestamp: 0n,
    latestTimestamp: 0n,
    unreadCount: 0,
}, undefined, notEq);
export const pinnedChatsStore = derived([serverPinnedChatsStore, chatDetailsLocalUpdates.pinnedToScopes], ([serverPinnedChats, localUpdates]) => {
    const mergedPinned = new Map(serverPinnedChats);
    for (const [chatId, updates] of localUpdates.entries()) {
        for (const scope of updates.added) {
            // Copy: the server's array must not be mutated, or undoing the pin can't remove it
            const ids = [...(mergedPinned.get(scope) ?? [])];
            if (!ids.find((id) => chatIdentifiersEqual(id, chatId))) {
                ids.unshift(chatId);
            }
            mergedPinned.set(scope, ids);
        }
        for (const scope of updates.removed) {
            const ids = mergedPinned.get(scope) ?? [];
            mergedPinned.set(scope, ids.filter((id) => !chatIdentifiersEqual(id, chatId)));
        }
    }
    return mergedPinned;
});
export const serverDirectChatBotsStore = writable(new Map(), undefined, notEq);
export const serverStreakInsuranceStore = writable({
    daysInsured: 0,
    daysMissed: 0,
}, undefined, notEq);
export const referralsStore = writable([], undefined, notEq);
export const streakInsuranceStore = derived([serverStreakInsuranceStore, localUpdates.streakInsurance], ([serverStreakInsurance, localUpates]) => localUpates ?? serverStreakInsurance);
export const allServerChatsStore = derived([
    serverGroupChatsStore,
    serverDirectChatsStore,
    serverCommunitiesStore,
    localUpdates.previewCommunities,
    localUpdates.uninitialisedDirectChats,
    localUpdates.groupChatPreviews,
], ([serverGroupChats, serverDirectChats, serverCommunities, previewCommunities, uninitialisedDirectChats, groupChatPreviews,]) => {
    const groupChats = serverGroupChats.values();
    const directChats = serverDirectChats.values();
    const channels = [...serverCommunities.values()].flatMap((c) => c.channels);
    const all = ChatMap.fromList([...groupChats, ...directChats, ...channels]);
    const previewChannels = ChatMap.fromList([...previewCommunities.values()].flatMap((c) => c.channels));
    const done = all
        .merge(uninitialisedDirectChats)
        .merge(groupChatPreviews)
        .merge(previewChannels);
    return done;
});
export const userMetricsStore = derived(allServerChatsStore, (allServerChats) => {
    const empty = emptyChatMetrics();
    return allServerChats.reduce((res, [_, chat]) => {
        return mergeChatMetrics(res, chat.membership?.myMetrics ?? empty);
    }, empty);
});
export const unreadFavouriteCountsStore = derived([serverFavouritesStore, allServerChatsStore, messagesRead], ([serverFavourites, allServerChats, _]) => {
    const chats = [...serverFavourites.values()].map((id) => allServerChats.get(id));
    return messagesRead.combinedUnreadCountForChats(chats);
});
export const favouritesVideoCallCountsStore = derived([serverFavouritesStore, allServerChatsStore], ([serverFavourites, allServerChats]) => {
    const chats = [...serverFavourites.values()].map((id) => allServerChats.get(id));
    return videoCallsInProgressForChats(chats);
});
export const selectedServerChatSummaryStore = derived([selectedChatIdStore, allServerChatsStore], ([selectedChatId, allServerChats]) => {
    return selectedChatId ? allServerChats.get(selectedChatId) : undefined;
});
// The updaters applied to chats in allChatsStore only ever assign top-level fields or fields
// of membership (nested objects such as permissions are replaced, never mutated), so a shallow
// copy plus a copy of membership is enough to keep the server chat untouched.
function shallowCloneChat(chat) {
    return { ...chat, membership: { ...chat.membership } };
}
function applyLocalUpdatesToChat(chat, updates) {
    if (updates === undefined || updates.isEmpty())
        return;
    chat.update((c) => {
        c.membership.notificationsMuted =
            updates.notificationsMuted ?? c.membership.notificationsMuted;
        c.membership.atEveryoneMuted = updates.atEveryoneMuted ?? c.membership.atEveryoneMuted;
        c.membership.archived = updates.archived ?? c.membership.archived;
        c.membership.rulesAccepted = updates.rulesAccepted ?? c.membership.rulesAccepted;
        const latestMessage = (updates?.latestMessage?.timestamp ?? BigInt(-1)) >
            (c.latestMessage?.timestamp ?? BigInt(-1))
            ? updates?.latestMessage
            : c.latestMessage;
        const latestEventIndex = Math.max(latestMessage?.index ?? 0, c.latestEventIndex);
        c.latestMessage = latestMessage;
        c.latestMessageIndex = latestMessage?.event?.messageIndex;
        c.latestEventIndex = latestEventIndex;
        if (c.kind !== "direct_chat") {
            c.frozen = updates.frozen ?? c.frozen;
            c.name = updates.name ?? c.name;
            c.description = updates.description ?? c.description;
            c.permissions = mergePermissions(c.permissions, updates?.permissions);
            c.gateConfig = updates.gateConfig ?? c.gateConfig;
            if (updates.eventsTTL !== undefined) {
                c.eventsTTL = applyOptionUpdate(c.eventsTTL, updates.eventsTTL);
            }
            c.public = updates.isPublic ?? c.public;
        }
    });
}
export const selectedChatBlockedOrSuspendedUsersStore = derived([
    blockedUsersStore,
    hideMessagesFromDirectBlocked,
    selectedChatBlockedUsersStore,
    selectedCommunityBlockedUsersStore,
    suspendedUsersStore,
], ([blockedUsers, hideMessagesFromDirectBlocked, selectedChatBlockedUsers, selectedCommunityBlockedUsers, suspendedUsers,]) => {
    const direct = hideMessagesFromDirectBlocked ? [...blockedUsers] : [];
    return new Set([
        ...selectedChatBlockedUsers,
        ...selectedCommunityBlockedUsers,
        ...suspendedUsers.keys(),
        ...direct,
    ]);
}, 
// this feeds allChatsStore and eventsStore so only publish when the membership changes
(a, b) => a?.size === b?.size && [...a].every((u) => b.has(u)));
// this is all server chats (which already include previews) + local updates applied.
export const allChatsStore = derived([
    allServerChatsStore,
    localUpdates.chats,
    chatSummaryLocalUpdates,
    currentUserIdStore,
    messageFiltersStore,
    selectedChatBlockedOrSuspendedUsersStore,
    messageLocalUpdates,
    localUpdates.unconfirmed,
], ([allServerChats, localChats, localUpdates, currentUserId, messageFilters, selectedChatBlockedOrSuspendedUsers, messageLocalUpdates, unconfirmed,]) => {
    const withUpdates = localChats.apply(allServerChats);
    return [...withUpdates.entries()].reduce((result, [chatId, chat]) => {
        const immutable = new Immutable(chat, shallowCloneChat);
        applyLocalUpdatesToChat(immutable, localUpdates.get(chat.id));
        mergeUnconfirmedIntoSummary(immutable, (k) => k, currentUserId, messageLocalUpdates, selectedChatBlockedOrSuspendedUsers, messageFilters, unconfirmed);
        result.set(chatId, immutable.value());
        return result;
    }, new ChatMap());
});
export const favouritesStore = derived([serverFavouritesStore, localUpdates.favourites], ([serverFavourites, local]) => {
    return local.apply(serverFavourites);
});
// all chats filtered by scope including previews and local updates
// the final client view of chat summaries with all updates merged in
export const chatSummariesStore = derived([chatListScopeStore, allChatsStore, favouritesStore], ([chatListScope, allChats, favourites]) => {
    switch (chatListScope.kind) {
        case "community": {
            const communityId = chatListScope.id.communityId;
            return allChats.filter((c) => c.kind === "channel" && c.id.communityId === communityId);
        }
        case "chats":
            return allChats.filter((c) => c.kind === "direct_chat" || c.kind === "group_chat");
        case "favourite": {
            return [...favourites.values()].reduce((favs, chatId) => {
                const chat = allChats.get(chatId);
                if (chat !== undefined) {
                    favs.set(chat.id, chat);
                }
                return favs;
            }, new ChatMap());
        }
        default:
            return new ChatMap();
    }
});
export const chatSummariesListStore = derived([pinnedChatsStore, chatListScopeStore, chatSummariesStore], ([pinnedChats, chatListScope, chatSummaries]) => {
    const pinnedByScope = pinnedChats.get(chatListScope.kind) ?? [];
    const pinned = pinnedByScope.reduce((result, id) => {
        const summary = chatSummaries.get(id);
        if (summary !== undefined) {
            result.push(summary);
        }
        return result;
    }, []);
    const pinnedIds = new Set(pinnedByScope.map(chatIdentifierToString));
    // compute the (bigint) display date once per chat rather than twice per comparison
    const unpinned = [];
    for (const chat of chatSummaries.values()) {
        if (!pinnedIds.has(chatIdentifierToString(chat.id))) {
            unpinned.push([getDisplayDate(chat), chat]);
        }
    }
    unpinned.sort(([a], [b]) => (a === b ? 0 : a < b ? 1 : -1));
    return pinned.concat(unpinned.map(([, chat]) => chat));
});
export const selectedChatSummaryStore = derived([selectedChatIdStore, chatSummariesStore], ([selectedChatId, chatSummaries]) => {
    if (selectedChatId === undefined)
        return undefined;
    return chatSummaries.get(selectedChatId);
});
export const proposalTopicsStore = derived([selectedChatSummaryStore, snsFunctionsStore], ([selectedChatSummary, snsFunctions]) => {
    if (selectedChatSummary !== undefined &&
        selectedChatSummary.kind !== "direct_chat" &&
        selectedChatSummary.subtype !== undefined) {
        if (selectedChatSummary.subtype.isNns) {
            return new Map([
                [1, "Neuron Management"],
                [3, "Network Economics"],
                [4, "Governance"],
                [5, "Node Admin"],
                [6, "Participant Management"],
                [7, "Subnet Management"],
                [8, "Network Canister Management"],
                [9, "KYC"],
                [10, "Node Provider Rewards"],
                [12, "Subnet Replica Version Management"],
                [13, "Replica Version Management"],
                [14, "SNS & Neurons' Fund"],
                [15, "API Boundary Node Management"],
                [16, "Subnet Rental"],
                [17, "Protocol Canister Management"],
                [18, "Service Nervous System Management"],
            ]);
        }
        else {
            const snsFunctionsMap = snsFunctions.get(selectedChatSummary.subtype.governanceCanisterId);
            if (snsFunctionsMap !== undefined) {
                return new Map([...snsFunctionsMap].slice(1).map((e) => [e[0], e[1].name]));
            }
        }
    }
    return new Map();
});
export const isProposalGroupStore = derived(selectedChatSummaryStore, (selectedChatSummary) => {
    return (selectedChatSummary !== undefined &&
        selectedChatSummary.kind !== "direct_chat" &&
        selectedChatSummary.subtype?.kind === "governance_proposals");
});
export const threadsByChatStore = derived(chatSummariesStore, (chatSummaries) => {
    return [...chatSummaries.entries()].reduce((result, [_, chat]) => {
        if ((chat.kind === "group_chat" || chat.kind === "channel") &&
            chat.membership &&
            chat.membership.latestThreads.length > 0) {
            result.set(chat.id, chat.membership.latestThreads);
        }
        return result;
    }, new ChatMap());
});
export const numberOfThreadsStore = derived(threadsByChatStore, (threadsByChat) => threadsByChat.map((_, ts) => ts.length).reduce((total, [_, n]) => total + n, 0));
export const threadsFollowedByMeStore = derived(threadsByChatStore, (threadsByChat) => {
    return threadsByChat.reduce((result, [chatId, threads]) => {
        const set = new Set();
        for (const thread of threads) {
            set.add(thread.threadRootMessageIndex);
        }
        result.set(chatId, set);
        return result;
    }, new ChatMap());
});
export const messagePermissionsForSelectedChatStore = derived(selectedChatSummaryStore, (selectedChatSummary) => {
    return getMessagePermissionsForSelectedChat(selectedChatSummary, "message");
});
export const threadPermissionsForSelectedChatStore = derived(selectedChatSummaryStore, (selectedChatSummary) => {
    return getMessagePermissionsForSelectedChat(selectedChatSummary, "thread");
});
export const selectedChatDraftMessageStore = derived([selectedChatIdStore, localUpdates.draftMessages], ([selectedChatId, draftMessages]) => selectedChatId ? draftMessages.get({ chatId: selectedChatId }) : undefined);
export const communityChannelVideoCallCountsStore = derived(communitiesStore, (communities) => {
    return [...communities.entries()].reduce((map, [id, community]) => {
        map.set(id, videoCallsInProgressForChats(community.channels));
        return map;
    }, new CommunityMap());
});
export const groupVideoCallCountsStore = derived(serverGroupChatsStore, (serverGroupChats) => {
    return videoCallsInProgressForChats([...serverGroupChats.values()]);
});
export const directVideoCallCountsStore = derived(serverDirectChatsStore, (serverDirectChats) => {
    return videoCallsInProgressForChats([...serverDirectChats.values()]);
});
export const directAndGroupVideoCallCountsStore = derived([serverDirectChatsStore, serverGroupChatsStore], ([serverDirectChats, serverGroupChats]) => {
    return videoCallsInProgressForChats([
        ...serverDirectChats.values(),
        ...serverGroupChats.values(),
    ]);
});
// Merged events plus the DRange of loaded indexes computed by the same pass;
// eventsStore / eventIndexesLoadedStore are projections of this.
const mergedEventsStore = derived([
    serverEventsStore,
    expiredServerEventRanges,
    selectedChatIdStore,
    localUpdates.failedMessages,
    localUpdates.unconfirmed,
    translationsStore,
    selectedChatBlockedOrSuspendedUsersStore,
    messageLocalUpdates,
    localUpdates.recentlySentMessages,
    messageFiltersStore,
], ([serverEvents, expiredEventRanges, selectedChatId, failedMessages, unconfirmedMessages, translations, selectedChatBlockedOrSuspendedUsers, messageLocalUpdates, recentlySentMessages, messageFilters,]) => {
    if (selectedChatId === undefined) {
        const range = new DRange();
        range.add(expiredEventRanges);
        return { events: [], range };
    }
    const ctx = { chatId: selectedChatId };
    const failedState = failedMessages.get(ctx);
    const failed = failedState ? [...failedState.values()] : [];
    const unconfirmedState = unconfirmedMessages.get(ctx);
    const unconfirmed = unconfirmedState ? [...unconfirmedState.values()] : [];
    return mergeEventsAndLocalUpdatesWithRange(serverEvents, [...unconfirmed, ...failed], expiredEventRanges, translations, selectedChatBlockedOrSuspendedUsers, messageLocalUpdates, recentlySentMessages, messageFilters);
});
export const eventsStore = derived(mergedEventsStore, (merged) => merged.events);
export const confirmedEventIndexesLoadedStore = derived([serverEventsStore, expiredServerEventRanges], ([events, expiredEventRanges]) => {
    const ranges = new DRange();
    events.forEach((e) => ranges.add(e.index));
    ranges.add(expiredEventRanges);
    return ranges;
});
export const eventIndexesLoadedStore = derived(mergedEventsStore, (merged) => merged.range);
export const messageActivitySummaryStore = derived([serverMessageActivitySummaryStore, localUpdates.messageActivityFeedReadUpTo], ([serverMessageActivitySummary, readUpTo]) => {
    if (readUpTo !== undefined && readUpTo >= serverMessageActivitySummary.latestTimestamp) {
        return {
            ...serverMessageActivitySummary,
            unreadCount: 0,
        };
    }
    return serverMessageActivitySummary;
});
export const directChatBotsStore = derived([serverDirectChatBotsStore, localUpdates.directChatBots], ([serverDirectChatBots, local]) => {
    return local.apply(serverDirectChatBots);
});
export const unreadGroupCountsStore = derived([serverGroupChatsStore, messagesRead], ([serverGroupChats, _]) => {
    return messagesRead.combinedUnreadCountForChats(serverGroupChats.values());
});
export const unreadDirectCountsStore = derived([serverDirectChatsStore, messagesRead], ([serverDirectChats, _]) => {
    return messagesRead.combinedUnreadCountForChats(serverDirectChats.values());
});
export const unreadDirectAndGroupCountsStore = derived([unreadDirectCountsStore, unreadGroupCountsStore], ([direct, group]) => {
    return mergePairOfCombinedUnreadCounts(direct, group);
});
export const unreadCommunityChannelCountsStore = derived([serverCommunitiesStore, messagesRead], ([serverCommunities, _]) => {
    return serverCommunities.reduce((map, [id, community]) => {
        map.set(id, messagesRead.combinedUnreadCountForChats(community.channels));
        return map;
    }, new CommunityMap());
});
export const globalUnreadCountStore = derived([unreadGroupCountsStore, unreadDirectCountsStore, unreadCommunityChannelCountsStore], ([unreadGroupCounts, unreadDirectCounts, unreadCommunityChannelCounts]) => {
    return mergeListOfCombinedUnreadCounts([
        unreadGroupCounts,
        unreadDirectCounts,
        mergeListOfCombinedUnreadCounts(Array.from(unreadCommunityChannelCounts.values())),
    ]);
});
const mergedThreadEventsStore = derived([
    serverThreadEventsStore,
    selectedThreadIdStore,
    localUpdates.failedMessages,
    localUpdates.unconfirmed,
    translationsStore,
    selectedChatBlockedOrSuspendedUsersStore,
    messageLocalUpdates,
    localUpdates.recentlySentMessages,
    messageFiltersStore,
], ([serverEvents, selectedThreadId, failedMessages, unconfirmedMessages, translations, selectedChatBlockedOrSuspendedUsers, messageLocalUpdates, recentlySentMessages, messageFilters,]) => {
    if (selectedThreadId === undefined)
        return { events: [], range: new DRange() };
    const ctx = selectedThreadId;
    const failedState = failedMessages.get(ctx);
    const failed = failedState ? [...failedState.values()] : [];
    const unconfirmedState = unconfirmedMessages.get(ctx);
    const unconfirmed = unconfirmedState ? [...unconfirmedState.values()] : [];
    return mergeEventsAndLocalUpdatesWithRange(serverEvents, [...unconfirmed, ...failed], new DRange(), translations, selectedChatBlockedOrSuspendedUsers, messageLocalUpdates, recentlySentMessages, messageFilters);
});
export const threadEventsStore = derived(mergedThreadEventsStore, (merged) => merged.events);
export const confirmedThreadEventIndexesLoadedStore = derived(serverThreadEventsStore, (events) => {
    const ranges = new DRange();
    events.forEach((e) => ranges.add(e.index));
    return ranges;
});
export const threadEventIndexesLoadedStore = derived(mergedThreadEventsStore, (merged) => merged.range);
export const selectedThreadDraftMessageStore = derived([selectedThreadIdStore, localUpdates.draftMessages], ([selectedThreadId, draftMessages]) => selectedThreadId ? draftMessages.get(selectedThreadId) : undefined);
export const identityStateStore = writable({ kind: "loading_user", registering: false }, undefined, notEq);
export const failedMessagesStore = localUpdates.failedMessages;
export const unconfirmedStore = localUpdates.unconfirmed;
export const latestSuccessfulUpdatesLoop = writable(0);
